import { OrderType } from './../../enums/order-type.enum';
import { DictionaryValue } from './../dictionary/dictionaryValue';
import { BaseClass } from '../base-class';
import { Client } from '../client';
import { User } from '../user/user';
import { OrderStatus } from '@enums/order-status.enum';
import { Site } from '@classes/site';
import { Address } from '@classes/address';
import { OrderPatient } from './orderPatient';
import { OrderStatusChange } from './orderStatusChange';
import { OrderStep } from './orderStep';
import { Vehicle } from '@classes/vehicles/vehicle';

export class Order extends BaseClass {
  identifier: string;
  driver?: User;
  site: Site;
  urgency?: DictionaryValue;
  statusChanges: OrderStatusChange[];
  startDate?: Date;
  requestedDeliveryDate?: Date;
  dispatcherComment: string;
  sender: OrderStep;
  receivers: OrderStep[];
  ordererComment?: string;
  ordererPhone?: string;
  ordererName: string;
  status: OrderStatus;
  type: OrderType;
  callDate?: Date;
  timeSpent?: number;
  price: number;
  extraStepPrice: number;

  isDeleted?: boolean;

  transportTypes: DictionaryValue[];
  patients?: OrderPatient[];

  client?: Client;

  modifiedFields?: string[];

  tagScanned: string;
  arrivedOn?: Date;
  completedOn?: Date;
  scansPickedGroupedByContainer?: any = {};
  scansDeliveredGroupedByContainer?: any = {};

  isVerySoon?: boolean;

  vehicle?: Vehicle;
  name: string;
  code?: string;

  constructor();
  constructor(
    site: Site,
    urgency: DictionaryValue,
    startDate: Date,
    ordererPhone: string,
    ordererName: string,
    sender: OrderStep,
    receivers: OrderStep[],
    transportTypes: DictionaryValue[],
    type: OrderType,
    ordererComment?: string,
    callDate?: Date,
  );
  constructor(
    site?: Site,
    urgency?: DictionaryValue,
    startDate?: Date,
    ordererPhone?: string,
    ordererName?: string,
    sender?: OrderStep,
    receivers?: OrderStep[],
    transportTypes?: DictionaryValue[],
    type?: OrderType,
    ordererComment?: string,
    callDate?: Date,
  ) {
    super();

    this.site = site;
    this.urgency = urgency;
    this.startDate = startDate;
    this.ordererPhone = this.trim(ordererPhone);
    this.ordererName = this.trim(ordererName);
    this.sender = sender;
    this.receivers = receivers;
    this.ordererComment = this.trim(ordererComment);
    this.transportTypes = transportTypes;
    this.type = type;
    this.callDate = callDate;
  }

  static fromObject(object: Order): Order {
    return Object.assign(new Order(), {
      ...object,
      driver: object.driver ? User.fromObject(object.driver) : null,
      site: object.site ? Site.fromObject(object.site) : null,
      vehicle: object.vehicle ? Vehicle.fromObject(object.vehicle) : null,
      urgency: object.urgency
        ? DictionaryValue.fromObject(object.urgency)
        : null,
      sender: object.sender ? OrderStep.fromObject(object.sender) : null,
      receivers: object.receivers?.map((r) => OrderStep.fromObject(r)),
      client: object.client ? Client.fromObject(object.client) : null,
    });
  }

  get display(): string {
    return this.identifier;
  }

  public computePercentageCompleted(): number {
    const nbStep = this.computeNbStep();
    const nbStepDone = this.computeNbStepDone();

    const percentage = (nbStepDone / nbStep) * 100;
    const roundedPercentage = Math.round(percentage * 1) / 1;
    return roundedPercentage;
  }

  public computeNbStep() {
    // + 1 for sender
    return this.receivers.length + 1;
  }

  public computeNbStepDone() {
    let result = this.receivers.filter(
      (receiver) => receiver.completedOn,
    ).length;
    if (this.sender.completedOn) result++;

    return result;
  }

  public countNbDeliveryScan() {
    return (
      this.receivers
        .map((receiver) => receiver.countDeliveryScan())
        .reduce((previous, current) => previous + current, 0) +
      this.sender.countDeliveryScan()
    );
  }

  public countNbPickupScan() {
    return (
      this.receivers
        .map((receiver) => receiver.countPickupScan())
        .reduce((previous, current) => previous + current, 0) +
      this.sender.countPickupScan()
    );
  }

  public lastStepAddress(): string {
    if (!this.sender.completedOn) return '/';

    const receiverCount = this.receivers.length;

    if (receiverCount === 0 || !this.receivers[0].completedOn) {
      return this.sender.address.name;
    }

    for (let i = 0; i < receiverCount; i++) {
      if (!this.receivers[i].completedOn) {
        return i > 0
          ? this.receivers[i - 1].address.name
          : this.sender.address.name;
      }
    }

    return this.receivers[receiverCount - 1].address.name;
  }

  public nextStepAddress(): string {
    const senderCompleted = this.sender.completedOn;

    if (!senderCompleted) {
      return this.sender.address.name;
    }

    const firstNotCompletedReceiver = this.receivers.find(
      (item) => !item.completedOn,
    );

    if (!firstNotCompletedReceiver) {
      return '/';
    }

    return firstNotCompletedReceiver.address.name;
  }
}
